import React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '../../utils/cn';

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    className={cn(
      "z-50 overflow-hidden rounded-md border border-border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    )}
    {...props}
  />
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

// Help Icon component for consistent help tooltips
export function HelpTooltip({ content }: { content: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-muted text-muted-foreground hover:bg-muted/80 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          aria-label="Help"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="h-3 w-3"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z"
            />
          </svg>
        </button>
      </TooltipTrigger>
      <TooltipContent>
        <p>{content}</p>
      </TooltipContent>
    </Tooltip>
  );
}

// Pre-defined tooltips for common technical terms
export const TechnicalTerms = {
  VPN: "Virtual Private Network - Secure connection to access your files remotely",
  SMB: "Server Message Block - Windows file sharing protocol", 
  Samba: "Open-source implementation of SMB for file sharing",
  NFS: "Network File System - Linux/Unix file sharing protocol",
  LDAP: "Lightweight Directory Access Protocol - Centralized user management",
  FTP: "File Transfer Protocol - Simple file upload/download service",
  SSH: "Secure Shell - Encrypted terminal access to your server",
  DNS: "Domain Name System - Translates domain names to IP addresses",
  SSL: "Secure Sockets Layer - Encryption for secure connections",
  TLS: "Transport Layer Security - Modern encryption protocol",
  RAID: "Redundant Array of Independent Disks - Data protection through disk mirroring",
  IPv4: "Internet Protocol version 4 - Standard network address format (e.g., 192.168.1.1)",
  IPv6: "Internet Protocol version 6 - New network address format",
  Bandwidth: "Data transfer speed and capacity",
  Latency: "Network response time (lower is better)",
  Throughput: "Actual data transfer rate",
  Firewall: "Security system that filters network traffic",
  Port: "Network connection endpoint (like a door number)",
  Proxy: "Intermediary server for requests",
  Gateway: "Network node that connects different networks",
  Subnet: "Subdivision of an IP network",
  VLAN: "Virtual Local Area Network - Logical network separation",
  Mount: "Connect a network drive to your computer",
  Share: "Folder accessible over the network",
  Quota: "Storage space limit for users",
  Permissions: "Access rights (read, write, execute)",
  Authentication: "Verifying user identity",
  Authorization: "Granting access to resources", 
  Encryption: "Scrambling data for security",
  Certificate: "Digital identity verification",
  "2FA": "Two-Factor Authentication - Extra security layer",
  Backup: "Copy of data for recovery",
  Snapshot: "Point-in-time copy of system state",
  Rsync: "Efficient file synchronization tool",
  Cron: "Task scheduler for automated jobs",
  Container: "Isolated computing environment",
  "CT ID": "Container ID - Unique identifier for your service instance",
  "OVPN": "OpenVPN configuration file for connecting to VPN",
  "Public Link": "Shareable web link for file access without login",
  "User Group": "Collection of users with shared permissions",
  "Disk Usage": "Amount of storage space currently used",
  "Service Status": "Current operational state of your service"
};

interface TermTooltipProps {
  term: keyof typeof TechnicalTerms;
  children: React.ReactNode;
  className?: string;
}

export function TermTooltip({ term, children, className }: TermTooltipProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={cn("underline decoration-dotted cursor-help", className)}>
          {children}
        </span>
      </TooltipTrigger>
      <TooltipContent>
        <p className="max-w-xs">{TechnicalTerms[term]}</p>
      </TooltipContent>
    </Tooltip>
  );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };